/**
 * A colleague call's room, named from the provider app's side.
 *
 * Same arrangement as `visit-room.ts`, and the same sharp edge:
 * `@repo/conferencing` may not contain a clinical noun, so the scope word is
 * spelled by each end independently. The server spells it in
 * `packages/api/src/queries/colleague-call.query.ts` (`CALL_ROOM_SCOPE`), and a
 * drift between the two gives both parties a room of their own — each connects,
 * each surface reads "Connected", and neither can see or hear the other.
 * Nothing throws and nothing logs.
 *
 * `__tests__/call-room.test.ts` reads the server's constant out of that file
 * and fails the PR if this one drifts from it.
 */

import { type RoomKey, roomKey } from "@repo/conferencing";
import { enterCall, leaveCall } from "@repo/conferencing/components";
import { callAnswerStore } from "./call-answer-store";

/** Must equal `CALL_ROOM_SCOPE` in `colleague-call.query.ts`. Guarded by test. */
export const CALL_ROOM_SCOPE = "call";

export const callRoomKey = (callId: string): RoomKey => roomKey(CALL_ROOM_SCOPE, callId);

/**
 * The call id a room key is for, or `undefined` when the key is not a call's.
 * One store holds every room; a host that assumed otherwise would address the
 * first visit room to land in it as a call.
 */
export function callIdFromRoomKey(key: RoomKey): string | undefined {
  const prefix = `${CALL_ROOM_SCOPE}:`;
  return key.startsWith(prefix) ? key.slice(prefix.length) || undefined : undefined;
}

/**
 * Enter a call's room.
 *
 * HOST, for both parties. Two colleagues are peers — #1243 settled that on the
 * server, where both `start` and `answer` mint a host credential — and asking
 * for a guest token here would be asking for one the server does not issue.
 */
export const openCallRoom = (callId: string): void => enterCall(callRoomKey(callId), "host");

/**
 * Enter it as the CALLER, who is ringing out rather than joining anybody.
 *
 * Minimised, because the room holds them and an empty chair: the full surface
 * would take the whole window — and the chart under it — for a call nobody has
 * picked up. The card names who is being rung and carries the hang-up, and the
 * package opens the surface when the colleague is present in the meeting.
 */
export const openOutgoingCallRoom = (callId: string): void => {
  enterCall(callRoomKey(callId), "host", { minimised: true });
  // Both parties are hosts, and either may minimise later. Remember who placed
  // the ring at entry so Leave never asks the recipient to abandon it.
  callAnswerStore.send({ type: "outgoing", callId });
};

/** Leave it. Keyed, so a stale caller cannot close the room already moved to. */
export const closeCallRoom = (callId: string): void => leaveCall(callRoomKey(callId));
